import { GetStaticPathsResult, GetStaticPropsContext, GetStaticPropsResult } from 'next';
import invariant from 'tiny-invariant';

import { getBlogPostBySlug } from '../../../src/api/contentful/blog/getBlogPostBySlug';
import { getBlogPostSlugs } from '../../../src/api/contentful/blog/getBlogPostSlugs';
import { getLatestBlogPostsExcludingCurrent } from '../../../src/api/contentful/blog/getLatestBlogPostsExcludingCurrent';
import { getPreviewBlogPost } from '../../../src/api/contentful/blog/getPreviewBlogPost';
import { getRelatedBlogPostsExcludingCurrent } from '../../../src/api/contentful/blog/getRelatedBlogPostsExcludingCurrent';
import { AppLogic } from '../../../src/components/AppLogic/AppLogic';
import { GlobalLayout } from '../../../src/components/Layouts/GlobalLayout';
import {
  BLOG_PAGE_NAV_LINKS,
  BLOG_POSTS_PER_PAGE,
  BLOG_POSTS_PREBUILD_PAGES,
} from '../../../src/constants/constants';
import { NOT_FOUND_WITH_REVALIDATION, REVALIDATE } from '../../../src/constants/next';
import { reportMykissError } from '../../../src/helpers/errorHandling';
import { IPageMetadata } from '../../../src/interfaces/nextjs';
import { BlogPost, IBlogPostProps } from '../../../src/views/Blog/BlogPost/BlogPost';

type IParams = {
  slug: string;
  categorySlug: string;
};

export async function getStaticProps({
  params,
  preview = false,
}: GetStaticPropsContext<IParams>): Promise<GetStaticPropsResult<IPageMetadata & IBlogPostProps>> {
  invariant(params, 'Params not found');

  try {
    const post = preview
      ? await getPreviewBlogPost(params.slug)
      : await getBlogPostBySlug(params.slug);

    const categoryId = post.fields.category?.sys.id;

    const relatedPosts = categoryId
      ? await getRelatedBlogPostsExcludingCurrent(params.slug, categoryId)
      : { items: [] };

    const latestPosts = await getLatestBlogPostsExcludingCurrent(params.slug);

    return {
      props: {
        _page: 'blog_post',
        _pageTitle: 'Blog Post',
        _pageId: post.sys.id,
        _pageSubject: post.fields.title,
        post,
        relatedPosts: relatedPosts.items,
        latestPosts: latestPosts.items,
        isPreview: preview,
      },
      revalidate: REVALIDATE.HOURLY,
    };
  } catch (error) {
    reportMykissError(error);
    return NOT_FOUND_WITH_REVALIDATION;
  }
}

export async function getStaticPaths(): Promise<GetStaticPathsResult<IParams>> {
  const slugs = await getBlogPostSlugs(BLOG_POSTS_PREBUILD_PAGES * BLOG_POSTS_PER_PAGE);

  return {
    paths: slugs.map(params => ({ params })),
    fallback: 'blocking',
  };
}

BlogPost.getLayout = function getLayout(page: any) {
  return (
    <AppLogic pageProps={page.props}>
      <GlobalLayout siteSubNavigationLinks={BLOG_PAGE_NAV_LINKS}>{page}</GlobalLayout>
    </AppLogic>
  );
};

export default BlogPost;
